/**
 * Subscription Store — Manages Pro subscription state
 *
 * Listens to the `users/{uid}.subscription` field in Firestore
 * and exposes tier/status/entitlements reactively.
 *
 * Feature gates use `useIsPro()` selector for clean access.
 */

import { create } from 'zustand';

// ============================================
// Types (must match functions/src/stripe.ts)
// ============================================

export type SubscriptionTier = 'free' | 'pro' | 'team';
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | 'none';

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
}

// ============================================
// Store
// ============================================

interface SubscriptionState {
  // State
  subscription: SubscriptionInfo;
  isLoading: boolean;
  error: string | null;

  // Actions
  startListening: (uid: string) => void;
  stopListening: () => void;
  refreshSubscription: (uid: string) => Promise<void>;
  setCheckoutLoading: (loading: boolean) => void;

  // Checkout state
  isCheckoutLoading: boolean;
}

let _unsubscribe: (() => void) | null = null;

const DEFAULT_SUBSCRIPTION: SubscriptionInfo = {
  tier: 'free',
  status: 'none',
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
};

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: { ...DEFAULT_SUBSCRIPTION },
  isLoading: false,
  error: null,
  isCheckoutLoading: false,

  /**
   * Start listening to the user's subscription field in Firestore.
   * Called when user signs in.
   */
  startListening: (uid: string) => {
    // Stop any existing listener
    get().stopListening();
    set({ isLoading: true, error: null });

    // Async setup — fire-and-forget
    (async () => {
      try {
        const { getFirestore } = await import('../lib/firebase');
        const db = await getFirestore();
        const { doc, onSnapshot } = await import('firebase/firestore');

        const userRef = doc(db, 'users', uid);
        _unsubscribe = onSnapshot(
          userRef,
          (snapshot) => {
            if (!snapshot.exists()) {
              set({ subscription: { ...DEFAULT_SUBSCRIPTION }, isLoading: false });
              return;
            }

            const data = snapshot.data();
            const sub = data?.subscription;

            if (!sub) {
              set({ subscription: { ...DEFAULT_SUBSCRIPTION }, isLoading: false });
              return;
            }

            set({
              subscription: {
                tier: sub.tier || 'free',
                status: sub.status || 'none',
                currentPeriodEnd: sub.currentPeriodEnd?.toDate?.() || null,
                cancelAtPeriodEnd: sub.cancelAtPeriodEnd || false,
              },
              isLoading: false,
            });
          },
          (error) => {
            console.error('[subscription] Listener error:', error);
            set({ error: error.message, isLoading: false });
          }
        );
      } catch (error) {
        console.error('[subscription] Failed to start listener:', error);
        set({
          error: error instanceof Error ? error.message : 'Failed to load subscription',
          isLoading: false,
        });
      }
    })();
  },

  /**
   * Stop listening to subscription changes.
   * Called on sign-out.
   */
  stopListening: () => {
    if (_unsubscribe) {
      _unsubscribe();
      _unsubscribe = null;
    }
    set({ subscription: { ...DEFAULT_SUBSCRIPTION }, isLoading: false, error: null });
  },

  /**
   * One-shot refresh of subscription data.
   */
  refreshSubscription: async (uid: string) => {
    try {
      const { getFirestore } = await import('../lib/firebase');
      const db = await getFirestore();
      const { doc, getDoc } = await import('firebase/firestore');

      const userRef = doc(db, 'users', uid);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        set({ subscription: { ...DEFAULT_SUBSCRIPTION } });
        return;
      }

      const sub = snapshot.data()?.subscription;
      if (!sub) {
        set({ subscription: { ...DEFAULT_SUBSCRIPTION } });
        return;
      }

      set({
        subscription: {
          tier: sub.tier || 'free',
          status: sub.status || 'none',
          currentPeriodEnd: sub.currentPeriodEnd?.toDate?.() || null,
          cancelAtPeriodEnd: sub.cancelAtPeriodEnd || false,
        },
      });
    } catch (error) {
      console.error('[subscription] Failed to refresh:', error);
    }
  },

  setCheckoutLoading: (loading: boolean) => set({ isCheckoutLoading: loading }),
}));

// ============================================
// Selectors
// ============================================

/**
 * True if user has an active Pro or Team subscription.
 * Usage: const isPro = useSubscriptionStore(selectIsPro);
 */
export const selectIsPro = (state: SubscriptionState): boolean => {
  const { tier, status } = state.subscription;
  if (tier === 'free') return false;
  return status === 'active' || status === 'trialing';
};

/**
 * True if subscription is in a grace period (past_due but still accessible).
 */
export const selectIsGracePeriod = (state: SubscriptionState): boolean => {
  return state.subscription.status === 'past_due';
};

/**
 * The current tier name.
 */
export const selectTier = (state: SubscriptionState): SubscriptionTier => {
  return state.subscription.tier;
};

/**
 * Convenience hook: returns true if the user has Pro access
 * (active, trialing, or in grace period).
 */
export const selectHasProAccess = (state: SubscriptionState): boolean => {
  const { tier, status } = state.subscription;
  if (tier === 'free') return false;
  return status === 'active' || status === 'trialing' || status === 'past_due';
};
